import React, { useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';
import airports from '../data/airports';
import { useNavigate } from 'react-router-dom';
import CruiseHighlights from './Highlights';
import Renderads from './advertisement';
import { v4 as uuidv4 } from 'uuid';

// ---------------- AIRPORT SUGGESTIONS ----------------
const getSuggestions = (input) => {
  if (!input) return [];
  return airports
    .filter(
      (airport) =>
        airport.code.toLowerCase().startsWith(input.toLowerCase()) ||
        airport.name.toLowerCase().includes(input.toLowerCase())
    )
    .slice(0, 50);
};

const renderSuggestionsDropdown = (input, setInput, setShowSuggestions) => {
  const suggestions = getSuggestions(input);

  return (
    suggestions.length > 0 && (
      <ul className="absolute left-0 z-50 w-full bg-white border border-gray-300 rounded-xl shadow-2xl max-h-64 overflow-y-auto mt-1 text-sm">
        {suggestions.map((airport) => (
          <li
            key={airport.code}
            onMouseDown={() => {
              setInput(`${airport.name} (${airport.code})`);
              setShowSuggestions(false);
            }}
            className="px-4 py-3 cursor-pointer hover:bg-gray-100 text-[15px]"
          >
            <span className="font-semibold text-gray-900">{airport.name}</span>{' '}
            <span className="text-gray-500">({airport.code})</span>
          </li>
        ))}
      </ul>
    )
  );
};

// ---------------- MAIN COMPONENT ----------------
const RenderFlights = () => {
  const [tripType, setTripType] = useState('Round Trip');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);

  const [departureDate, setDepartureDate] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');

  // Travelers
  const [showTravelerDropdown, setShowTravelerDropdown] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState(1);

  const navigate = useNavigate();

  const swapLocations = () => {
    const temp = from;
    setFrom(to);
    setTo(temp);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const searchId = uuidv4();
    localStorage.setItem('search_id', searchId);

    navigate('/flightresults', {
      state: {
        from,
        to,
        departureDate,
        arrivalDate,
        tripType,
        adults,
        children,
        infants,
        cabinClass,
      },
    });
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen pb-14">
      <Renderads imgNumber={1} />

      <div className="max-w-7xl mx-auto mt-8 p-6 bg-white rounded-2xl shadow-xl border border-gray-200">
        {/* ---------------- TRIP TYPE BUTTONS ---------------- */}
        <div className="flex space-x-4 mb-6 bg-gray-100 p-2 rounded-full w-fit mx-auto">
          {['Round Trip', 'One Way'].map((type) => (
            <button
              key={type}
              onClick={() => setTripType(type)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition duration-200 ${
                tripType === type
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* ---------------- SEARCH FORM ---------------- */}
        <form onSubmit={handleSearch}>
          <div className="flex flex-wrap md:flex-nowrap gap-4 items-end">

            {/* FROM INPUT */}
            <div className="relative w-full md:w-[22%]">
              <label className="text-xs text-gray-600 font-semibold mb-1 block">
                From
              </label>
              <input
                type="text"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setShowFromSuggestions(true);
                }}
                onBlur={() => setTimeout(() => setShowFromSuggestions(false), 150)}
                onFocus={() => setShowFromSuggestions(true)}
                className="border border-gray-300 rounded-xl px-4 py-3 w-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Enter departure"
                required
              />
              {showFromSuggestions &&
                renderSuggestionsDropdown(from, setFrom, setShowFromSuggestions)}
            </div>

            {/* SWAP BUTTON */}
            <button
              type="button"
              onClick={swapLocations}
              className="p-3 bg-gray-100 rounded-full shadow-md hover:bg-gray-200 transition"
            >
              <FaExchangeAlt className="text-gray-700" />
            </button>

            {/* TO INPUT */}
            <div className="relative w-full md:w-[22%]">
              <label className="text-xs text-gray-600 font-semibold mb-1 block">
                To
              </label>
              <input
                type="text"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setShowToSuggestions(true);
                }}
                onBlur={() => setTimeout(() => setShowToSuggestions(false), 150)}
                onFocus={() => setShowToSuggestions(true)}
                className="border border-gray-300 rounded-xl px-4 py-3 w-full shadow-sm focus:ring-2 focus:ring-blue-400"
                placeholder="Enter destination"
                required
              />
              {showToSuggestions &&
                renderSuggestionsDropdown(to, setTo, setShowToSuggestions)}
            </div>

            {/* DEPARTURE DATE */}
            <div className="w-full md:w-[18%]">
              <label className="text-xs text-gray-600 font-semibold mb-1 block">
                Departure Date
              </label>
              <input
                type="date"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                className="border border-gray-300 rounded-xl px-4 py-3 w-full shadow-sm focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* RETURN DATE (if round trip) */}
            {tripType === 'Round Trip' && (
              <div className="w-full md:w-[18%]">
                <label className="text-xs text-gray-600 font-semibold mb-1 block">
                  Return Date
                </label>
                <input
                  type="date"
                  value={arrivalDate}
                  onChange={(e) => setArrivalDate(e.target.value)}
                  className="border border-gray-300 rounded-xl px-4 py-3 w-full shadow-sm focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>
            )}

            {/* TRAVELERS DROPDOWN */}
            <div className="relative w-full md:w-[15%]">
              <label className="text-xs text-gray-600 font-semibold mb-1 block">
                Travelers
              </label>

              <div
                onClick={() => setShowTravelerDropdown(!showTravelerDropdown)}
                className="border border-gray-300 rounded-xl px-4 py-3 w-full bg-white shadow-sm cursor-pointer"
              >
                {adults + children + infants} Traveler
                {adults + children + infants > 1 ? 's' : ''}
              </div>

              {showTravelerDropdown && (
                <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl p-4 w-72 z-50 border border-gray-200">
                  {/* ADULTS */}
                  {[ 
                    { label: 'Adult (18+)', value: adults, setValue: setAdults, min: 1 },
                    { label: 'Child (2-17)', value: children, setValue: setChildren, min: 0 },
                    { label: 'Infant (0-2)', value: infants, setValue: setInfants, min: 0 },
                  ].map(({ label, value, setValue, min }) => (
                    <div key={label} className="flex justify-between items-center mb-3">
                      <span className="text-sm">{label}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="w-8 h-8 rounded-lg bg-gray-200 text-lg hover:bg-gray-300"
                          onClick={() => setValue(Math.max(value - 1, min))}
                        >
                          –
                        </button>
                        <span className="w-5 text-center">{value}</span>
                        <button
                          type="button"
                          className="w-8 h-8 rounded-lg bg-gray-200 text-lg hover:bg-gray-300"
                          onClick={() => setValue(value + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* CABIN CLASS */}
                  <select
                    value={cabinClass}
                    onChange={(e) => setCabinClass(Number(e.target.value))}
                    className="mt-3 w-full border border-gray-300 rounded-lg p-2 bg-gray-50"
                  >
                    <option value={1}>Economy</option>
                    <option value={2}>Premium Economy</option>
                    <option value={3}>Business</option>
                    <option value={4}>First</option>
                  </select>
                </div>
              )}
            </div>

            {/* SEARCH BUTTON */}
            <div className="w-full md:w-[12%] min-w-[100px]">
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition"
              >
                Search Flights
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Spacing + Highlights */}
      <div className="mt-12"></div>
      <CruiseHighlights />
    </div>
  );
};

export default RenderFlights;
